import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Drawer,
  Grid,
  Theme,
  Typography,
  createStyles,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { NetworkIndicator, colors } from 'design-system';
import { uniqueId } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AddIcon from '@material-ui/icons/Add';

// Data Layer
import { QuotationSummaryResponse } from 'data-layer-cr/dist/models';

// Components
import FilterTag from '../../../FilterTag';
import { QuotationHistoryLayout } from '../History';
import { VinModificationDrawer } from '../VinModification';
import FileHandler from './components/FileHandler';
import QuotationListCard from './components/QuotationListCard';
import QuotationTable from './components/QuotationTable';

// resources
import search_girl_warning from '../../../../resources/search_girl_warning.svg';

// Icons
import { GridIcon, ListIcon } from '../../../Icons';

// Models
import { FilterOption } from '../../../../models/Filter';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingLeft: 16,
      paddingRight: 16,
    },
    detailsDialogue: {
      position: 'relative',
      borderRadius: 10,
      minWidth: 380,
      padding: 0,
    },
    dialogContent: {
      padding: '0 !important',
    },
    quotationListNotFound: {
      [theme.breakpoints.up('sm')]: {
        margin: 0,
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      },
    },
    quotationListNotFoundText: {
      fontFamily: 'Noto Sans SC',
      fontStyle: 'normal',
      fontWeight: 'bold',
      fontSize: 16,
      lineHeight: '23px',
      color: colors.silver,
      paddingTop: 20,
    },
    quotationSvg: {
      [theme.breakpoints.down('xs')]: {
        height: '15em',
      },
    },
    contentDrawer: {
      height: '100%',
    },
    '@supports ( -moz-appearance:none )': {
      contentDrawer: {
        height: '100%',
        [theme.breakpoints.up('xs')]: {
          paddingRight: 17,
        },
      },
    },
    anchor: {
      width: '100%',
      height: '100px',
      opacity: '0',
    },

    rightButtonChooseDisplayMode: {
      borderTopRightRadius: 10,
      borderBottomRightRadius: 10,
    },
    leftButtonChooseDisplayMode: {
      borderTopLeftRadius: 10,
      borderBottomLeftRadius: 10,
    },
    barSelectDisplayMode: {
      marginTop: 15,
      marginBottom: 15,
    },
    buttonCreateQuotation: {
      height: 50,
      minWidth: 'max-content',
      borderRadius: 80,
      background: colors.bright_turquoise,
      color: 'white',
      paddingLeft: 15,
      paddingRight: 20,
      '&:hover': {
        background: 'white',
        color: colors.bright_turquoise,
      },
    },
  }),
);

function makeFilterTags(
  tagFilterList: FilterOption[],
  handleRemoveFilter: (filter: FilterOption) => void,
): JSX.Element[] {
  return tagFilterList.map((filter) => (
    <div key={`filter-tag-${filter.categoryId}-${filter.id}`}>
      <FilterTag
        data-testid={`filter-tag-${filter.categoryId}-${filter.id}`}
        inputId={`filter-tag-${filter.categoryId}-${filter.id}`}
        filter={filter}
        onClick={handleRemoveFilter}
      />
    </div>
  ));
}

export enum DisplayModes {
  TableMode = 'table',
  CardMode = 'card',
}

type Props = {
  quotationList: QuotationSummaryResponse[];
  isLoading: boolean;
  handleOpenNewQuotation: () => void;
  handleRemoveFilter: (filter: FilterOption) => void;
  tagFilterList: FilterOption[];
  anchorRef: (node?: Element | null | undefined) => void;
  setCurentMode: (displayMode: DisplayModes) => void;
  currentMode: DisplayModes;
};

const QuotationsList = ({
  quotationList,
  isLoading,
  anchorRef,
  handleOpenNewQuotation,
  tagFilterList,
  handleRemoveFilter,
  setCurentMode,
  currentMode,
}: Props): JSX.Element => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [vinChangeHash, setVinChangeHash] = useState('');
  const [currentHash, updateCurrentHash] = useState('');

  const history = useNavigate();

  function detailsIsOpen(): boolean {
    return currentHash !== '';
  }

  function closeDialogue(): void {
    updateCurrentHash('');
  }

  const cardPressed = useCallback(
    (quotationHash: string): void => {
      if (isMobile) {
        history(`/quotationHistory/${quotationHash}`);
      } else {
        updateCurrentHash(quotationHash);
      }
    },
    [history, isMobile],
  );

  const closeVinDrawer = () => {
    setVinChangeHash('');
  };

  const openVinDrawer = useCallback(
    (quotationHash: string): void => {
      if (isMobile) {
        history(`/vinModification/${quotationHash}`);
      } else {
        setVinChangeHash(quotationHash);
      }
    },
    [history, isMobile],
  );

  const MemoQuotationListCard = useMemo(
    () =>
      quotationList.map(
        (curQuotationSummary, index): React.ReactNode => (
          <Grid item xs={12} md={6} lg={4} key={uniqueId()}>
            <QuotationListCard
              id={`sales-web-div-open-quotation-${index}`}
              data-testid="quotationListCard"
              onClick={(): void => cardPressed(curQuotationSummary.urlSalt)}
              quotationSummary={curQuotationSummary}
              onModificateVin={(): void =>
                openVinDrawer(curQuotationSummary.urlSalt)
              }
            />
          </Grid>
        ),
      ),
    [quotationList, cardPressed, openVinDrawer],
  );

  const MemoQuotationListTable = useMemo(
    () => (
      <QuotationTable
        data-testid="quotation-list-table"
        cardPressed={cardPressed}
        quotationList={quotationList}
        onModificateVin={openVinDrawer}
      />
    ),
    [quotationList, cardPressed, openVinDrawer],
  );

  const isInCardMode = isMobile || currentMode === DisplayModes.CardMode;

  return (
    <>
      <FileHandler />
      <div className={classes.root}>
        {/* Not Found Section */}
        {!isLoading && quotationList.length <= 0 && (
          <div className={classes.quotationListNotFound}>
            <Grid item xs={12} container justifyContent="center">
              <img
                src={search_girl_warning}
                alt="not found"
                className={classes.quotationSvg}
              />
            </Grid>
            <Grid item xs={12} container justifyContent="center">
              <Typography
                variant="h3"
                align="center"
                className={classes.quotationListNotFoundText}
                data-testid="error-quotation-empty"
              >
                No se encontraron cotizaciones.
              </Typography>
            </Grid>
          </div>
        )}

        {/* Mode display Section, Table or List only on Desktop */}
        {!isMobile && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            className={classes.barSelectDisplayMode}
          >
            <ButtonGroup color="default">
              <Button
                data-testid="button-change-to-list-card-mode"
                onClick={() => setCurentMode(DisplayModes.CardMode)}
                className={classes.leftButtonChooseDisplayMode}
                style={{ background: isInCardMode ? colors.madison : 'white' }}
              >
                <GridIcon
                  stroke={isInCardMode ? 'white' : colors.bright_turquoise}
                />
              </Button>
              <Button
                data-testid="button-change-to-table-mode"
                onClick={() => setCurentMode(DisplayModes.TableMode)}
                className={classes.rightButtonChooseDisplayMode}
                style={{ background: isInCardMode ? 'white' : colors.madison }}
              >
                <ListIcon
                  stroke={isInCardMode ? colors.bright_turquoise : 'white'}
                />
              </Button>
            </ButtonGroup>
            {/* Tag filter area */}
            <Box display="flex" flexWrap="wrap" flexGrow={1}>
              {makeFilterTags(tagFilterList, handleRemoveFilter)}
            </Box>

            {/* Create Quotation */}
            <Button
              startIcon={<AddIcon />}
              data-testid="button-create-quotation"
              id="sales-web-button-new-quotation"
              onClick={handleOpenNewQuotation}
              className={classes.buttonCreateQuotation}
              size="large"
            >
              Nueva Cotización
            </Button>
          </Box>
        )}

        <NetworkIndicator
          data-testid="network-indicator"
          message="Obteniendo cotizaciones..."
          // do not show when a list is already shown
          requesting={isLoading && quotationList.length <= 0}
        />

        {/* Quotation List Section */}
        <Grid container spacing={2}>
          {isInCardMode ? MemoQuotationListCard : MemoQuotationListTable}

          {/* Display spinner while list is loading */}
          {isLoading && quotationList.length > 0 && (
            <Grid item xs={12} container justifyContent="center">
              <CircularProgress data-testid="scroll-spinner" />
            </Grid>
          )}
        </Grid>

        {/* infinite scroll anchor */}
        <div className={classes.anchor} ref={anchorRef} data-testid="anchor" />

        {/* Quotation History Drawer Section */}
        <Drawer
          data-testid="drawer"
          anchor="right"
          open={detailsIsOpen()}
          onClick={closeDialogue}
        >
          <div
            onClick={(e): void => e.stopPropagation()}
            onKeyDown={(e): void => e.stopPropagation()}
            data-testid="drawer-content"
            role="none"
            className={classes.contentDrawer}
          >
            <QuotationHistoryLayout
              data-testid="history-drawer"
              quotationHash={currentHash}
              onCloseClicked={closeDialogue}
            />
          </div>
        </Drawer>

        <VinModificationDrawer
          data-testid="vin-modification-drawer"
          quotationHash={vinChangeHash}
          onClose={closeVinDrawer}
          open={vinChangeHash !== ''}
        />
      </div>
    </>
  );
};

export default QuotationsList;
